import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAccounts } from "../lib/accounts";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Wallet, ArrowLeftRight, Crown, Eraser, MoveRight, Pencil, Check, X } from "lucide-react";
import HelpButton from "../components/HelpButton";
import { fmtMoney, todayISO } from "../lib/format";
import ReconcileBadge from "../components/ReconcileBadge";

const COLORS = ["#d9d4f1", "#b5e4ca", "#fdecc8", "#ffb3b3", "#a3d5ff", "#f4b4d9"];

export default function Accounts() {
  const { accounts, selectedId, select, reload } = useAccounts();
  const { user } = useAuth();
  const isPaid = user?.modules?.multi_account !== false;
  const [form, setForm] = useState({ name: "", starting_balance: "", color: COLORS[0] });
  const [xfer, setXfer] = useState({
    from_account_id: "",
    to_account_id: "",
    amount: "",
    date: todayISO(),
    memo: "",
  });
  const [busy, setBusy] = useState(false);
  const transferFormRef = useRef(null);
  const [latestReconciles, setLatestReconciles] = useState({});
  // Which account is currently in "rename" mode. Only one at a time so a
  // partially-typed change is never lost by accident when tapping another row.
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const startRename = (a) => {
    setEditingId(a.id);
    setEditName(a.name);
  };
  const cancelRename = () => {
    setEditingId(null);
    setEditName("");
  };
  const saveRename = async () => {
    const next = editName.trim();
    if (!next) {
      toast.error("Name cannot be empty");
      return;
    }
    if (next.length > 60) {
      toast.error("Name too long");
      return;
    }
    try {
      await api.put(`/accounts/${editingId}`, { name: next });
      toast.success("Renamed");
      cancelRename();
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reconcile/latest");
        setLatestReconciles(data || {});
      } catch (e) {
        // Non-blocking; badges will show "Never reconciled" state instead.
        console.warn("Failed to load reconcile history:", e?.message);
      }
    })();
  }, [accounts]);

  const startTransfer = (fromId) => {
    // Prefill "From"; pick first other account as sensible "To" default
    const other = accounts.find((a) => a.id !== fromId);
    setXfer((prev) => ({
      ...prev,
      from_account_id: fromId,
      to_account_id: prev.to_account_id && prev.to_account_id !== fromId ? prev.to_account_id : (other?.id || ""),
    }));
    setTimeout(() => {
      transferFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      // Focus amount for fast typing
      const el = transferFormRef.current?.querySelector('[data-testid="xfer-amount"]');
      el?.focus();
    }, 50);
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounts", {
        name: form.name,
        starting_balance: parseFloat(form.starting_balance || "0"),
        color: form.color,
      });
      setForm({ name: "", starting_balance: "", color: COLORS[0] });
      await reload();
      toast.success("Account added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this account? Transactions will move to the first remaining account."))
      return;
    try {
      await api.delete(`/accounts/${id}`);
      await reload();
      if (selectedId === id) select("");
      toast.success("Account deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const wipeAccount = async (acc, importedOnly) => {
    const scope = importedOnly ? "CSV-imported" : "ALL";
    const msg = importedOnly
      ? `Wipe CSV-imported transactions from "${acc.name}"?\n\nKeeps: manual + receipt-OCR txns, the account, budgets, recurring bills.\nThis cannot be undone.`
      : `Wipe ALL transactions from "${acc.name}"?\n\nKeeps: the account itself, budgets, recurring bills.\nDeletes: every transaction in this account (manual, imported, and receipt-OCR).\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(
        `/accounts/${acc.id}/transactions${importedOnly ? "?imported_only=true" : ""}`
      );
      toast.success(`Wiped ${data.deleted} ${scope} txn${data.deleted === 1 ? "" : "s"} from ${acc.name}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wipe failed");
    }
  };

  const moveAllTxns = async (fromAcc) => {
    const others = accounts.filter((x) => x.id !== fromAcc.id);
    if (others.length === 0) {
      toast.error("You need another account to move transactions into");
      return;
    }
    // Build a picker prompt — window.prompt with numbered choices for max simplicity
    const list = others.map((a, i) => `${i + 1}) ${a.name}`).join("\n");
    const pick = window.prompt(
      `Move ALL transactions from "${fromAcc.name}" to which account?\n\n${list}\n\nEnter the number:`
    );
    if (!pick) return;
    const idx = parseInt(pick, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= others.length) {
      toast.error("Invalid selection");
      return;
    }
    const target = others[idx];
    if (!window.confirm(`Move ALL transactions from "${fromAcc.name}" to "${target.name}"?\n\nThis reassigns every txn — nothing is deleted.`)) return;
    try {
      const { data } = await api.post(`/accounts/${fromAcc.id}/move-transactions`, {
        to_account_id: target.id,
        scope: "all",
      });
      toast.success(`Moved ${data.moved} txn${data.moved === 1 ? "" : "s"} to ${target.name}`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    }
  };

  const transfer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/transfers", {
        ...xfer,
        amount: parseFloat(xfer.amount),
      });
      toast.success("Transfer posted to both registers");
      setXfer({ ...xfer, amount: "", memo: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="accounts-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Accounts</h1>
        <HelpButton id="accounts" />
      </div>

      <div className="nb-card overflow-hidden">
        {accounts.map((a) => (
          <div
            key={a.id}
            data-testid={`account-item-${a.name}`}
            className="p-4 border-b-2 border-[#1a1a1a]/10 last:border-0 flex items-center justify-between"
            style={{ background: a.color }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border-2 border-[#1a1a1a] bg-white flex items-center justify-center">
                <Wallet size={20} strokeWidth={3} />
              </div>
              <div>
                {editingId === a.id ? (
                  <form
                    onSubmit={(e) => { e.preventDefault(); saveRename(); }}
                    className="flex items-center gap-1"
                    data-testid={`rename-form-${a.id}`}
                  >
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") cancelRename(); }}
                      className="nb-input !py-1 !text-base !w-40"
                      data-testid={`rename-input-${a.id}`}
                      maxLength={60}
                    />
                    <button
                      type="submit"
                      data-testid={`rename-save-${a.id}`}
                      className="nb-btn nb-btn-dark !py-1 !px-2"
                      title="Save"
                    >
                      <Check size={14} strokeWidth={3} />
                    </button>
                    <button
                      type="button"
                      onClick={cancelRename}
                      data-testid={`rename-cancel-${a.id}`}
                      className="nb-btn nb-btn-secondary !py-1 !px-2"
                      title="Cancel"
                    >
                      <X size={14} strokeWidth={3} />
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="font-display font-black text-lg">{a.name}</div>
                    <button
                      onClick={() => startRename(a)}
                      data-testid={`rename-account-${a.name}`}
                      className="p-1 rounded hover:bg-black/5"
                      title={`Rename ${a.name}`}
                    >
                      <Pencil size={12} strokeWidth={3} className="text-[#4a4a4a]" />
                    </button>
                  </div>
                )}
                <div className="text-xs font-bold text-[#4a4a4a]">
                  Start: {fmtMoney(a.starting_balance)}
                </div>
                <div className="mt-1">
                  <ReconcileBadge
                    latest={latestReconciles[a.id]}
                    testid={`reconcile-badge-${a.name}`}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={`select-account-${a.name}`}
                onClick={() => select(a.id)}
                className={`chip !py-1 !text-xs ${selectedId === a.id ? "active" : ""}`}
              >
                {selectedId === a.id ? "Selected" : "Select"}
              </button>
              {isPaid && accounts.length >= 2 && (
                <button
                  data-testid={`transfer-from-${a.name}`}
                  onClick={() => startTransfer(a.id)}
                  className="nb-btn nb-btn-secondary !py-1 !px-2"
                  title={`Transfer from ${a.name}`}
                >
                  <ArrowLeftRight size={14} strokeWidth={3} />
                </button>
              )}
              <button
                data-testid={`wipe-account-${a.name}`}
                onClick={() => wipeAccount(a, false)}
                className="nb-btn nb-btn-secondary !py-1 !px-2"
                title={`Wipe ALL transactions in ${a.name} (keeps the account)`}
              >
                <Eraser size={14} strokeWidth={3} />
              </button>
              {accounts.length >= 2 && (
                <button
                  data-testid={`move-txns-${a.name}`}
                  onClick={() => moveAllTxns(a)}
                  className="nb-btn nb-btn-secondary !py-1 !px-2"
                  title={`Move ALL transactions in ${a.name} to another account`}
                >
                  <MoveRight size={14} strokeWidth={3} />
                </button>
              )}
              <button
                data-testid={`delete-account-${a.name}`}
                onClick={() => remove(a.id)}
                className="nb-btn nb-btn-secondary !py-1 !px-2"
              >
                <Trash2 size={14} strokeWidth={3} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={add} className="nb-card p-5 space-y-3" data-testid="add-account-form">
        <div className="label !mb-0">Add account</div>
        {!isPaid && accounts.length >= 1 ? (
          <div
            className="p-4 rounded-xl border-2 border-[#1a1a1a] flex flex-col items-center text-center gap-2"
            style={{ background: "linear-gradient(135deg,#fdecc8,#d9d4f1)" }}
            data-testid="multi-account-locked"
          >
            <Crown size={28} strokeWidth={2.5} />
            <div className="font-display font-black text-lg">Pro feature</div>
            <div className="text-xs font-medium text-[#4a4a4a]">
              Multiple accounts and inter-account transfers are part of SyncBook Pro. Ask your admin
              to upgrade your plan to unlock them.
            </div>
          </div>
        ) : (
          <>
            <input
              data-testid="account-name"
              required
              className="nb-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Savings, Cash, etc."
            />
            <input
              data-testid="account-starting-balance"
              type="number"
              step="0.01"
              className="nb-input"
              value={form.starting_balance}
              onChange={(e) => setForm({ ...form, starting_balance: e.target.value })}
              placeholder="Starting balance (0.00)"
            />
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  className="w-8 h-8 rounded-lg border-2 border-[#1a1a1a]"
                  style={{
                    background: c,
                    boxShadow: form.color === c ? "2px 2px 0 0 #1a1a1a" : "none",
                  }}
                  aria-label={c}
                />
              ))}
            </div>
            <button type="submit" data-testid="add-account-btn" className="nb-btn nb-btn-dark w-full !py-3">
              <Plus size={16} strokeWidth={3} /> Add account
            </button>
          </>
        )}
      </form>

      {isPaid && accounts.length >= 2 && (
        <form ref={transferFormRef} onSubmit={transfer} className="nb-card p-5 space-y-3" data-testid="transfer-form">
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={18} strokeWidth={3} />
            <div className="label !mb-0">Transfer between accounts</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">From</label>
              <select
                data-testid="xfer-from"
                required
                className="nb-select"
                value={xfer.from_account_id}
                onChange={(e) => setXfer({ ...xfer, from_account_id: e.target.value })}
              >
                <option value="">Choose</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">To</label>
              <select
                data-testid="xfer-to"
                required
                className="nb-select"
                value={xfer.to_account_id}
                onChange={(e) => setXfer({ ...xfer, to_account_id: e.target.value })}
              >
                <option value="">Choose</option>
                {accounts
                  .filter((a) => a.id !== xfer.from_account_id)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              data-testid="xfer-amount"
              type="number"
              step="0.01"
              required
              className="nb-input"
              value={xfer.amount}
              onChange={(e) => setXfer({ ...xfer, amount: e.target.value })}
              placeholder="Amount"
            />
            <input
              data-testid="xfer-date"
              type="date"
              required
              className="nb-input"
              value={xfer.date}
              onChange={(e) => setXfer({ ...xfer, date: e.target.value })}
            />
          </div>
          <input
            data-testid="xfer-memo"
            className="nb-input"
            value={xfer.memo}
            onChange={(e) => setXfer({ ...xfer, memo: e.target.value })}
            placeholder="Memo (optional)"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="submit-transfer-btn"
            className="nb-btn nb-btn-dark w-full !py-3"
          >
            <ArrowLeftRight size={16} strokeWidth={3} /> {busy ? "Posting…" : "Post transfer"}
          </button>
        </form>
      )}
    </div>
  );
}
